/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Inject, Optional } from '@angular/core';

import { IAdvancedFormEvent } from '~shared/modules/advanced-form/models/advanced-form.model';

import { ADVANCED_FORM_CONFIG, GA_TRACKING_LABEL } from '../../constants/idam-register.constant';
import { AuthorizationFormClassDirective } from '../../classes/authorization-form.class';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { AuthorizationService } from '../../services/authorization.service';
import { AuthService } from '../../services/authorization-idam.service';
import { MmAlertService, MmDialogService } from '@metromarkets/components';
import { TranslateService } from '@ngx-translate/core';
import { take, takeUntil } from 'rxjs/operators';
import { CSSHttpError } from '~shared/modules/error-handler/models/css-error.model';
import { TIER1_SIGNUP_LEGAL_EXTRA_LINKS, TIER1_SIGNUP_LEGAL_TEXT_PARAMS } from '~shared/constants/legal-text.constants';
import { ActivatedRoute, Router, UrlSerializer } from '@angular/router';
import { ScrollUtils } from '~shared/utils/scroll.utils';
import { ErrorsUtil } from '~shared/utils/errors.util';
import { IdamRegisterTrackingService } from '~app/modules/authorization/services/idam-register-tracking.service';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';

// prettier-ignore
import {
  TierOneRegistrationPromptService
} from '~app/modules/authorization/services/tier.one.registration.prompt.service';
import { HOTJAR_EVENT_LIST } from '~app/modules/authorization/constants/hotjar-event-name-list.constant';
import { APP_USER_VALIDATE_EMAIL_ROUTE, CountryCode, NUMBER_1 } from '~shared/constants';
import { TierOneFormValidationService } from '~app/modules/authorization/services/tier-one-form-validation.service';
import { ValidateEmailGaTrackingService } from '~app/modules/authorization/services/validate-email-ga-tracking.service';
import { CheckEmailService } from '~app/modules/authorization/services/check.email.service';
import isEmpty from 'lodash.isempty';
import { ORDER_LINK } from '~shared/constants/links.constants';
// prettier-ignore
import {
  RedirectFromTierOneSignupDialogComponent
// eslint-disable-next-line max-len
} from '~app/modules/authorization/components/redirect-from-tier-one-signup-dialog/redirect-from-tier-one-signup-dialog.component'; // NOSONAR
import { FF_LOGIN_OR_PASSWORD_RESET } from '~app/modules/authorization/constants/authorization.constant';
import { ExperimentService } from '~app/modules/authorization/services/experiment.service';
import { EMAIL_FIELD_VALIDATION_CONFIG } from '../../constants/validate-email.constant';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';

declare const hj;

@Component({
  selector: 'app-idam-register',
  templateUrl: './idam-register.component.html',
  styleUrls: ['./idam-register.component.scss'],
})
export class IDAMRegisterComponent extends AuthorizationFormClassDirective {
  public advancedFormConfig = ADVANCED_FORM_CONFIG;
  public TIER1_SIGNUP_LEGAL_TEXT_PARAMS;
  public TIER1_SIGNUP_LEGAL_EXTRA_LINKS;
  public currentCountry;
  public isTier1ExtraLinks$;

  isBusiness = false;
  disableEmailField = false;
  validatedEmail;
  showSpinner = false;
  isPasswordless = false;
  backUrlForEmailPrefilling: boolean;

  constructor(
    authorizationService: AuthorizationService,
    activeFeaturesConfigcatService: ActiveFeaturesConfigcatService,
    applicationEventService: ApplicationEventService,
    private authService: AuthService,
    private alertService: MmAlertService,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private serverLoggerService: ServerLoggerService,
    protected idamRegisterTrackingService: IdamRegisterTrackingService,
    private tierOneRegistrationPromptService: TierOneRegistrationPromptService,
    private tierOneFormValidationService: TierOneFormValidationService,
    private registerWithEmailGaTrackingService: ValidateEmailGaTrackingService,
    private checkEmailService: CheckEmailService,
    private serializer: UrlSerializer,
    private dialogService: MmDialogService,
    private experimentService: ExperimentService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any,
  ) {
    super(authorizationService, activeFeaturesConfigcatService, applicationEventService);

    this.currentCountry = getCountryDomainCode(this.webAppBuyerRequest);
  this.isTier1ExtraLinks$ = this.currentCountry === CountryCode.Germany
    || this.currentCountry === CountryCode.Netherlands
    || this.currentCountry === CountryCode.France;

    try {
      hj('event', HOTJAR_EVENT_LIST.tierOneSignup);
    } catch (error) {}


    this.TIER1_SIGNUP_LEGAL_TEXT_PARAMS = TIER1_SIGNUP_LEGAL_TEXT_PARAMS();
    this.TIER1_SIGNUP_LEGAL_EXTRA_LINKS = TIER1_SIGNUP_LEGAL_EXTRA_LINKS();

    const { registerBusiness, validatedEmail, backUrl = '/' } = this.route.snapshot.queryParams;
    this.validatedEmail = validatedEmail;

    this.experimentService.isExperimentEnabled$.subscribe((isEnabled) => {
      this.backUrlForEmailPrefilling = isEnabled ? true : backUrl.includes(ORDER_LINK) === true;
    });

    if (this.validatedEmail && this.backUrlForEmailPrefilling) {
      this.isEmailPrefilled(true);
    }

    this.advancedFormConfig = {
      ...ADVANCED_FORM_CONFIG,
      initialFormData: {
        registerBusiness: registerBusiness === 'true',
      },
    };
  }

  public handleSubmitEvent(event: IAdvancedFormEvent): void {
    const { email, password, registerBusiness } = event.payload.formData;

    this.authService.register(email.trim(), password, registerBusiness, this.isPasswordless).subscribe(
      () => {
        // Point of no return: we know for sure that starting from here a redirect will happen
        // so we show a spinner until that time
        this.showSpinner = true;
        // old flow, we avoid calling tier 1 account created event in business flow
        if (!registerBusiness) {
          this.idamRegisterTrackingEvent(this.isBusiness);
        }
        if (!registerBusiness) {
          this.tierOneRegistrationPromptService.promptSuccessFulRegistrationMessage();
        }

        return event.done();
      },
      (error: CSSHttpError) => {
        ScrollUtils.scrollTop();

        let hasInvalidEmail = false;
        if (error.message.includes('Error validating the request body')) {
          hasInvalidEmail = error.invalidParams?.userPrincipalName?.type === 'email';
          if (hasInvalidEmail) {
            error.invalidParams[EMAIL_FIELD_VALIDATION_CONFIG.name] = error.invalidParams.userPrincipalName;
          }
        }

        this.handleRegistrationFail(error, email, hasInvalidEmail);

        if (error.message.includes('is already in use')) {
          this.handleAlreadyExistsEmail(email);
        }
        return event.done(error.invalidParams);
      }
    );
  }

  handleRegistrationFail(cssHttpError: CSSHttpError, email: string, hasInvalidEmail = false): void {
    const message = `{ ${hasInvalidEmail ? 'Email not valid - ' : ''}${cssHttpError?.message.replace(email, '*****')}}`;
    this.idamRegisterTrackingService.idamSignUpErrorEventTracking(message);
  }

  public handleValidationEvent(event: IAdvancedFormEvent): void {
    const errors = ErrorsUtil.collectFormErrors(event.payload);
    const message = ErrorsUtil.concatErrorsToString(errors);
    this.idamRegisterTrackingService.idamSignUpErrorEventTracking(message);
    this.tierOneFormValidationService.logTierOneFormValidationError(message, event.payload.value);

    if (message.includes('email - Empty')) {
      try {
        hj('event', HOTJAR_EVENT_LIST.emptyEmailOnTierOneSignUp);
      } catch (error) {
        this.serverLoggerService.error('TIER1 HotJar Not Available', { error });
      }
    }
  }

  private isEmailPrefilled(validatedEmail) {
    this.checkEmailService.currentEmail$.pipe(takeUntil(this.unsubscribe$)).subscribe((userEmail) => {
      if (!validatedEmail) {
        userEmail = '';
      }

      if (isEmpty(userEmail)) {
        const { queryParams } = this.router.parseUrl(this.router.url);
        const { backUrl = '/' } = queryParams;

        const urlTree = this.router.createUrlTree([APP_USER_VALIDATE_EMAIL_ROUTE], { queryParams: { backUrl } });
        this.router.navigateByUrl(this.serializer.serialize(urlTree));
      } else {
        this.disableEmailField = true;
      }
    });
  }

  private handleAlreadyExistsEmail(email) {
    this.activeFeaturesConfigcatService
      .isFeatureEnabled(FF_LOGIN_OR_PASSWORD_RESET)
      .pipe(take(NUMBER_1))
      .subscribe((isLoginOrPasswordDialogEnabled) => {
        if (isLoginOrPasswordDialogEnabled) {
          const dialogRef = this.dialogService.open(RedirectFromTierOneSignupDialogComponent, { data: email });

          dialogRef.afterClosed().subscribe((buttonType) => {
            const label = buttonType === 'login' ? GA_TRACKING_LABEL.goToLogin : GA_TRACKING_LABEL.goToPasswordReset;
            this.idamRegisterTrackingService.idamSignUpEventTrackingForAlreadyExistingAccount(label);
            this.handleLoginOrPasswordReset(buttonType, email);
          });
        } else {
          this.handleAlreadyExistsEmailAlert();
        }
      });
  }

  private handleLoginOrPasswordReset(buttonType: string, email) {
    if (buttonType === 'cancel') {
      this.handleAlreadyExistsEmailAlert();
      return;
    }
    const { queryParams } = this.router.parseUrl(this.router.url);

    this.authService.login(queryParams.backUrl, email, buttonType === 'forgotPassword');
  }

  private idamRegisterTrackingEvent(isBusiness): void {
    this.idamRegisterTrackingService.idamSignUpEventTracking(isBusiness);
  }

  private handleAlreadyExistsEmailAlert() {
    const titleEmailAlreadyExists = 'PAGES.AUTH.REGISTRATION.ALERT.EMAIL_EXISTS.TITLE';
    this.tierOneRegistrationPromptService.promptError(titleEmailAlreadyExists);
  }
}
